import { useElplanBids } from 'api/elplan/elplan.api'
import type { Bid } from 'api/elplan/elplan.types'

type UseDayWideBidLimitParams = {
  elplanInfoId?: number
  startTime: ISODateTime
  endTime?: ISODateTime
  scenarioId?: number
  partitionId?: number
  topicalBids?: Bid[]
  maxBlockAndProfileBids?: number
}

type UseDayWideBidLimitResult = {
  /** 'unresolved' while a limit applies but the day-wide count is still fetching or has errored — creation must stay disabled rather than trust the narrower topical count. */
  status: 'resolved' | 'unresolved'
  /** The day-wide block+profile count has reached the configured limit. Drives the "limit reached" tooltip. */
  reachedLimit: boolean
}

/**
 * Resolves the day-wide combined block/profile bid limit for the editor: whether creation must be
 * blocked because the configured limit is reached or its count is not yet trustworthy.
 *
 * A send takes only a date and a system, so it dispatches the whole day across every scenario and
 * partition. The editor's own topical read is scoped to one scenario (every new-module surface passes a
 * scenarioId; only the legacy Elhandel module passes neither and is already day-wide), so scoped
 * surfaces issue a second, unfiltered read here to match the send's reach. That read is shared by all
 * editors of the same day, so the count selects and guards explicitly rather than relying on the
 * cache happening to be empty.
 */
export function useDayWideBidLimit({
  elplanInfoId,
  startTime,
  endTime,
  scenarioId,
  partitionId,
  topicalBids,
  maxBlockAndProfileBids,
}: UseDayWideBidLimitParams): UseDayWideBidLimitResult {
  const needsDayWideRead = scenarioId !== undefined || partitionId !== undefined

  const {
    data: dayWideBids,
    isFetching,
    isError,
  } = useElplanBids(elplanInfoId, startTime, endTime, undefined, undefined, needsDayWideRead)

  const hasLimit = typeof maxBlockAndProfileBids === 'number'

  const status: UseDayWideBidLimitResult['status'] =
    hasLimit && needsDayWideRead && (isFetching || isError) ? 'unresolved' : 'resolved'

  const blockAndProfileCount =
    (needsDayWideRead ? dayWideBids : topicalBids)?.filter(
      (bid) => bid.type === 'blockbid' || bid.type === 'profilebid'
    ).length ?? 0
  const reachedLimit = hasLimit ? blockAndProfileCount >= maxBlockAndProfileBids : false

  return { status, reachedLimit }
}
